import { useState, useRef, useCallback, useEffect } from 'react';
import { usePortalStore, toast } from '../stores';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * useStreamChat — SSE streaming chat hook with 50ms token buffering via RAF
 */
export function useStreamChat() {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [sources, setSources] = useState([]);
  const [toolCalls, setToolCalls] = useState([]);
  const [activeTools, setActiveTools] = useState([]);
  const [memory, setMemory] = useState(null);
  const bufferRef = useRef('');
  const rafRef = useRef(null);
  const abortRef = useRef(null);

  // Flush buffer to state via requestAnimationFrame
  const flushBuffer = useCallback(() => {
    if (bufferRef.current) {
      const chunk = bufferRef.current;
      bufferRef.current = '';
      setStreamingContent(prev => prev + chunk);
    }
    rafRef.current = null;
  }, []);

  const scheduleFlush = useCallback(() => {
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(flushBuffer);
    }
  }, [flushBuffer]);

  const streamMessage = useCallback(async (messages, conversationId, model, kbIds, language) => {
    const { token, authType } = usePortalStore.getState();
    setIsStreaming(true);
    setStreamingContent('');
    setSources([]);
    setToolCalls([]);
    setActiveTools([]);
    setMemory(null);
    bufferRef.current = '';

    const controller = new AbortController();
    abortRef.current = controller;

    let result = { conversationId: null, usage: null, sources: [], toolCalls: [], content: '' };
    let fullContent = '';

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token && authType === 'jwt') headers.Authorization = `Bearer ${token}`;
      const response = await fetch(`${API_BASE}/api/portal/chat`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          messages,
          stream: true,
          conversation_id: conversationId,
          model: model || undefined,
          // Scope tenant-knowledge retrieval to the KBs picked in the
          // composer. Omitted/empty → server defaults to all KBs.
          kb_ids: Array.isArray(kbIds) && kbIds.length ? kbIds : undefined,
          // Preferred reply language — 'en' | 'ja' | 'ar'.
          language: language || undefined,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Chat request failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6);
          if (raw === '[DONE]') {
            flushBuffer();
            result.content = fullContent;
            setIsStreaming(false);
            return result;
          }
          try {
            const data = JSON.parse(raw);
            if (data.type === 'content') {
              bufferRef.current += data.text;
              fullContent += data.text;
              scheduleFlush();
            } else if (data.type === 'sources') {
              setSources(data.sources || []);
              result.sources = data.sources || [];
              // Capture the user-explicit KB scope so the assistant
              // bubble can render an "Answering from N KB(s)" chip.
              if (Array.isArray(data.kb_scope) && data.kb_scope.length) {
                result.kbScope = data.kb_scope;
              }
            } else if (data.type === 'memory') {
              const m = { compressed: data.compressed, messages_compressed: data.messages_compressed, recent_window: data.recent_window };
              setMemory(m);
              result.memory = m;
            } else if (data.type === 'tool_start') {
              setActiveTools(prev => [...prev, { tool: data.tool, args: data.args, status: 'running' }]);
            } else if (data.type === 'tool_result') {
              setActiveTools(prev => prev.map(t =>
                t.tool === data.tool && t.status === 'running'
                  ? { ...t, status: data.success ? 'success' : 'error', result: data.result }
                  : t
              ));
            } else if (data.type === 'tool_calls') {
              setToolCalls(data.tool_calls || []);
              result.toolCalls = data.tool_calls || [];
            } else if (data.type === 'done') {
              result.conversationId = data.conversation_id;
              result.usage = data.usage;
              result.meter = data.meter || null;
            } else if (data.type === 'error') {
              throw new Error(data.message);
            }
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr;
          }
        }
      }

      flushBuffer();
      result.content = fullContent;
      return result;
    } catch (err) {
      if (err.name === 'AbortError') {
        // User clicked Stop — preserve whatever tokens streamed so far so
        // the partial response is saved as the assistant's message instead
        // of vanishing on cancel.
        flushBuffer();
        result.content = fullContent;
        result.aborted = true;
        return result;
      }
      toast.error(err.message || 'Failed to send message');
      throw err;
    } finally {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [flushBuffer, scheduleFlush]);

  const abort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      abortRef.current?.abort();
    };
  }, []);

  return {
    streamMessage,
    isStreaming,
    streamingContent,
    sources,
    toolCalls,
    activeTools,
    memory,
    abort,
    resetStream: useCallback(() => {
      setStreamingContent('');
      setSources([]);
      setToolCalls([]);
      setActiveTools([]);
      setMemory(null);
    }, []),
  };
}

/**
 * useIntersectionAnimation — triggers callback when element enters viewport
 */
export function useIntersectionAnimation(options = {}) {
  const ref = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || hasAnimated) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasAnimated(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px', ...options }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasAnimated]);

  return [ref, hasAnimated];
}
